import { useState } from 'react';
import type { FormEvent } from 'react';
import { useMutation } from '@tanstack/react-query';
import { resetPassword } from '../lib/api';
import { useDocumentTitle } from '../hooks/useDocumentTitle';
import { connectionErrorMessage, getErrorStatus } from './authErrors';

const PASSWORD_MIN_LEN = 12;
const RESET_PATH_PREFIX = '/reset/';
const RESET_PASSWORD_ERROR_ID = 'reset-password-hint';
const RESET_CONFIRM_PASSWORD_ERROR_ID = 'reset-confirm-password-error';

export function extractTokenFromPath(pathname: string): string | null {
  if (!pathname.startsWith(RESET_PATH_PREFIX)) return null;
  const raw = pathname.slice(RESET_PATH_PREFIX.length);
  if (!raw) return null;
  const segment = raw.split('/')[0];
  if (!segment) return null;
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function ResetPasswordForm() {
  const token = extractTokenFromPath(window.location.pathname);
  useDocumentTitle('Reset password');
  const [password, setPassword] = useState('');
  const [passwordTouched, setPasswordTouched] = useState(false);
  const [confirmPassword, setConfirmPassword] = useState('');
  const [confirmPasswordTouched, setConfirmPasswordTouched] = useState(false);

  const mutation = useMutation({
    mutationFn: resetPassword,
  });

  if (!token) {
    return (
      <div className="login-form" role="alert">
        <h2>Missing reset token</h2>
        <p>
          This page expects a token in the URL (like
          <code> /reset/abc123</code>). Re-open the link from your email.
        </p>
        <a href="/forgot-password" className="login-form-submit">
          Request a new link
        </a>
      </div>
    );
  }

  function handleSubmit(e: FormEvent<HTMLFormElement>) {
    e.preventDefault();
    setPasswordTouched(true);
    setConfirmPasswordTouched(true);
    if (!token || !isValidResetPassword(password, confirmPassword)) return;
    mutation.mutate({ token, new_password: password });
  }

  if (mutation.isSuccess && mutation.data.reset) {
    return (
      <div className="login-form" role="status" aria-live="polite">
        <h2>Password reset</h2>
        <p className="login-form-subtitle">
          Your password has been updated. Sign in with your new password to
          continue.
        </p>
        <a href="/" className="login-form-submit">
          Sign in
        </a>
      </div>
    );
  }

  // Per F.13 anti-enumeration: server returns 200 OK with `{reset: false}` for
  // every failure mode (token-not-found / expired / consumed / weak-password).
  // UI shows a single generic message.
  if (mutation.isSuccess && !mutation.data.reset) {
    return (
      <div className="login-form" role="alert">
        <h2>Reset link can't be used</h2>
        <p>
          This reset link is invalid, expired, already used, or the password
          you chose is too weak. Request a new link and try again.
        </p>
        <a href="/forgot-password" className="login-form-submit">
          Request a new link
        </a>
      </div>
    );
  }

  const error = mutation.error;
  const passwordError =
    passwordTouched && password.length === 0
      ? 'Enter your new password.'
      : password.length > 0 && password.length < PASSWORD_MIN_LEN
        ? `Use at least ${PASSWORD_MIN_LEN} characters for your new password.`
        : null;
  const confirmPasswordError =
    confirmPasswordTouched && confirmPassword.length === 0
      ? 'Confirm your new password.'
      : confirmPassword.length > 0 && confirmPassword !== password
        ? 'Passwords do not match.'
        : null;
  const canSubmit = isValidResetPassword(password, confirmPassword);

  return (
    <form
      className="login-form"
      onSubmit={handleSubmit}
      aria-label="Reset password"
      noValidate
    >
      <h2>Choose a new password</h2>
      <p className="login-form-subtitle">
        Enter a new password to finish resetting your account.
      </p>

      {error && (
        <div className="login-form-error" role="alert" aria-live="assertive">
          {resetPasswordErrorMessage(error)}
        </div>
      )}

      <label className="login-form-field">
        <span>
          New password
          <span className="login-form-hint-inline">(min 12 chars)</span>
        </span>
        <input
          type="password"
          name="password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LEN}
          value={password}
          onChange={(e) => {
            setPassword(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={() => setPasswordTouched(true)}
          disabled={mutation.isPending}
          aria-invalid={passwordError ? true : undefined}
          aria-describedby={passwordError ? RESET_PASSWORD_ERROR_ID : undefined}
        />
        {passwordError && (
          <span
            id={RESET_PASSWORD_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {passwordError}
          </span>
        )}
      </label>

      <label className="login-form-field">
        <span>Confirm new password</span>
        <input
          type="password"
          name="confirm_password"
          autoComplete="new-password"
          required
          minLength={PASSWORD_MIN_LEN}
          value={confirmPassword}
          onChange={(e) => {
            setConfirmPassword(e.target.value);
            if (mutation.isError) mutation.reset();
          }}
          onBlur={() => setConfirmPasswordTouched(true)}
          disabled={mutation.isPending}
          aria-invalid={confirmPasswordError ? true : undefined}
          aria-describedby={
            confirmPasswordError ? RESET_CONFIRM_PASSWORD_ERROR_ID : undefined
          }
        />
        {confirmPasswordError && (
          <span
            id={RESET_CONFIRM_PASSWORD_ERROR_ID}
            className="login-form-field-error"
            aria-live="polite"
          >
            {confirmPasswordError}
          </span>
        )}
      </label>

      <button
        type="submit"
        className="login-form-submit"
        disabled={mutation.isPending || !canSubmit}
      >
        {mutation.isPending && (
          <span className="button-spinner" aria-hidden="true" />
        )}
        {mutation.isPending ? 'Saving…' : 'Reset password'}
      </button>

      <div className="login-form-links">
        <a href="/">Back to Sign in</a>
      </div>
    </form>
  );
}

function resetPasswordErrorMessage(error: unknown): string {
  const status = getErrorStatus(error);
  if (status === 400 || status === 422) {
    return 'Use the reset link from your email and a password with at least 12 characters.';
  }
  if (status === 429) {
    return 'Too many reset attempts. Wait a few minutes, then request a new link.';
  }
  if (status === null) return connectionErrorMessage();
  if (status >= 500) {
    return "We couldn't reset your password because the server had a problem. Try again in a minute.";
  }
  return "We couldn't reset your password. Request a new link, then try again.";
}

function isValidResetPassword(password: string, confirmPassword: string): boolean {
  return password.length >= PASSWORD_MIN_LEN && confirmPassword === password;
}
